<template>
  <section class="LicenseManagement">
    <Card class="LicenseManagement__card">
      <section class="LicenseManagement__mainSection">
        <div class="LicenseManagement__header">
          <Heading
            type="quaternary"
            level="2"
            :heading="messages.license.licensedUsers"
          />
        </div>
        <div class="LicenseManagement__inner">
          <SearchForm
            id="search"
            class="LicenseManagement__searchForm"
            name="search"
            :placeholder="messages.common.searchPlaceholder"
            :value="searchWord"
            @inputWord="searchWord = $event"
          />
          <ul class="LicenseManagement__list">
            <li class="LicenseManagement__listItem">
              <Button
                class="LicenseManagement__button"
                :label="messages.license.licenseGrant"
                dynamics="primary"
                tag="button"
                :isDisabled="
                  isListLoading || !licenseId || isLicenseAssignDialogOpen
                "
                @click="openLicenseAssignDialog()"
              />
            </li>
          </ul>
        </div>
        <SelectedSettings
          v-if="checkedItemsCount"
          v-model:isTableChecked="isThChecked"
          v-model:isAllSelected="isAllSelected"
          class="LicenseManagement__selectedSettings"
          :selectedNum="checkedItemsCount"
          :allNum="totalCount"
          :itemsPerPage="MAX_DISPLAY_NUM"
          :labelName="messages.license.licenseRelease"
          :onDeleteButtonClick="onClickDeleteModalOpen"
          @update:isAllSelected="toggleAllChecked"
        />
        <UserList
          v-model:isThChecked="isThChecked"
          :displayUsers="displayUsers"
          :isListLoading="
            !isLicenseAssignDialogOpen && (isListLoading || !licenseId)
          "
          :headList="headList"
          :noDataText="noDataText"
          :noDataImgUrl="noDataImageUrl"
          @onCheckHeader="onCheckHeader"
          @onCheckItem="onCheckItem"
        />
        <PaginationBar
          class="LicenseManagement__pagination"
          :currentPage="currentPage"
          :maxDisplayPages="maxDisplayPages"
          :itemsPerPage="MAX_DISPLAY_NUM"
          :total="totalCount"
          @onClick:pageNumber="movePage"
        />
      </section>

      <Modal
        v-if="isOpenModal"
        ref="modal"
        class="LicenseManagement__modal"
        :heading="heading"
        :size="isExtraSmallPixel ? 'extraSmall' : 'small'"
        @closeModal="onClickCancelButton"
      >
        <template v-if="!isError">
          <p class="LicenseManagement__modalText">
            {{ messages.license.confirm.deActivateMsg }}
          </p>

          <ul class="LicenseManagement__modalButton">
            <li class="LicenseManagement__modalItem">
              <Button
                :isDisabled="isUpdateLicenseLoading"
                :label="messages.common.cancel"
                dynamics="secondary"
                tag="button"
                @click="onClickCancelButton"
              />
            </li>
            <li class="LicenseManagement__modalItem">
              <Button
                :isDisabled="isUpdateLicenseLoading"
                :label="messages.common.release"
                dynamics="danger"
                tag="button"
                @click="onClickRemove"
              />
            </li>
          </ul>
        </template>
        <template v-else>
          <Icon class="LicenseManagement__icon" iconName="Exception"></Icon>
          <!-- safe usage of v-html: content is fully static and developer-controlled -->
          <p class="LicenseManagement__modalText" v-html="errorText"></p>
          <div class="LicenseManagement__modalButton">
            <Button
              :label="messages.common.close"
              dynamics="secondary"
              tag="button"
              @click="onClickDeleteClose"
            />
          </div>
        </template>
      </Modal>
      <LicenseAssignDialog
        v-if="isLicenseAssignDialogOpen"
        :assignableCount="assignableCount"
        :displayUsers="licenseNotAssignedUsers"
        :isListLoading="isListLoading"
        @onClickModalClose="onLicenseDialogClose"
        @onClickLicenseAssign="onClickLicenseAssign"
      >
      </LicenseAssignDialog>
    </Card>
  </section>
</template>

<script lang="ts">
export default {
  name: 'LicenseManagement',
};
</script>

<script lang="ts" setup>
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import Button from '@spectee/components/Atoms/Button/index.vue';
import Modal from '@spectee/components/Organisms/Modal/index.vue';
import Card from '@spectee/components/Atoms/Card/index.vue';
import Icon from '@spectee/components/Atoms/IconBase/index.vue';
import SearchForm from '@spectee/components/Atoms/SearchForm/index.vue';
import PaginationBar from '@spectee/components/Molecules/PaginationBar/index.vue';
import SelectedSettings from '@spectee/components/Molecules/SelectedSettings/index.vue';
import UserList from '@spectee/components/Organisms/UserList/index.vue';
import { Ref, computed, ref, watch, watchEffect } from 'vue';
import { useOverlayLoading } from '@spectee/composables/useOverlayLoading';
import LicenseAssignDialog from '@spectee/components/Organisms/LicenseAssignDialog/index.vue';
import { useListAllUsers } from '@spectee/graphql/queries/listAllUsers';
import {
  Message,
  Users,
  Action,
  UpdateLicensesInput,
  Group,
} from '@spectee/graphql/api';
import { useRoute, useRouter } from 'vue-router';
import { useFeedback } from '@spectee/composables/useFeedback';
import { useUpdateLicenses } from '@spectee/graphql/mutations/updateLicenses';
import { userStatus } from '@spectee/ts/userStatus';
import noDataImage from '@spectee/assets/images/img_notFound.png';
import noSearchDataImage from '@spectee/assets/images/img_search_noItem.svg';
import messages from '../../../../messages.json';
import { useWindowWidth } from '@spectee/composables/useWindowWidth';
import { UserItem } from '@spectee/types/atoms';
import { isNotNullish } from '@spectee/ts/isNotNullish';

const route = useRoute();
const router = useRouter();
const { isExtraSmallPixel } = useWindowWidth();

interface Props {
  licenseId: string;
  users: Users;
  licenseAssignmentCurrent: number;
  licenseAssignmentLimit: number;
}

const props = withDefaults(defineProps<Props>(), {
  users: Users.both,
});

const emit = defineEmits<{
  (e: 'onUpdateLicense'): void;
}>();

const usersNeeded = ref(props.users);
const licenseIdref = ref(props.licenseId);
const filterOption = ref('match');
const heading = ref('');
const errorText = ref('');

const licenseRemoveError = messages.license.error.licenseRemoveError;
const licenseAssignError = messages.license.error.licenseAssignError;
const maxLicenseLimitReachedError =
  messages.license.error.maxLicenseLimitReachedError;
const maxAuthorityLimitReachedError =
  messages.license.error.maxAuthorityLimitReachedError;

const assignableCount = computed(() => {
  return props.licenseAssignmentLimit - props.licenseAssignmentCurrent;
});

const MAX_DISPLAY_NUM = 50;
const maxDisplayPages = 5;
const currentPage = ref(1);
const isThChecked = ref(false);
const isAllSelected = ref(false);
const checkedItemsCount = ref(0);

const queryParams = route.query;

const headList = [
  { key: 'name', label: messages.common.headList.name },
  { key: 'displayName', label: messages.common.headList.displayName },
  { key: 'email', label: messages.common.headList.email },
  { key: 'groupName', label: messages.common.headList.groupName },
  { key: 'status', label: messages.common.headList.status },
  { key: 'authority', label: messages.common.headList.authority },
];

const isLicenseAssignDialogOpen = ref(false);

const openLicenseAssignDialog = async () => {
  if (assignableCount.value <= 0) {
    isOpenModal.value = true;
    isError.value = true;
    heading.value = messages.license.error.licenseAssignErrorHeading;
    errorText.value = maxLicenseLimitReachedError;
  } else {
    isLicenseAssignDialogOpen.value = true;
    if (filterOption.value === 'notMatch') {
      await listRefetch();
    } else {
      usersNeeded.value = Users.active;
      filterOption.value = 'notMatch';
    }
  }
};

const onLicenseDialogClose = () => {
  isLicenseAssignDialogOpen.value = false;
};

//// リストの取得・表示関係の処理

// ユーザー・招待の一覧
const licenseAssignedUsers: Ref<UserItem[]> = ref([]);
const licenseNotAssignedUsers: Ref<UserItem[]> = ref([]);

type UserStatus = (typeof userStatus)[keyof typeof userStatus];

watch(
  () => route.query,
  () => {
    if (Object.keys(route.query).length === 0) {
      router.push({
        path: route.path,
        query: { ...queryParams }, // Preserve existing query params
      });
    }
  }
);

const variables = computed(() => {
  return {
    filter: {
      users: usersNeeded.value,
      licenseId: {
        [filterOption.value]: licenseIdref.value,
      },
    },
  };
});

const userQueryOption = ref({ enabled: false, fetchPolicy: 'network-only' });

const {
  listAllUsersRes: listUsersRes,
  loading: isListLoading,
  refetch: listRefetch,
} = useListAllUsers(variables, userQueryOption);

watch(
  () => props.licenseId,
  async newVal => {
    if (newVal) {
      licenseIdref.value = newVal;
      if (!userQueryOption.value.enabled) {
        userQueryOption.value.enabled = true;
      }
    }
  }
);

// ユーザー・招待の一覧を取得、UserItem型に丸めて結合し、licenseAssignedUsersに格納
// licenseAssignedUsersの値は、取得時のほか、フィルタリング実行時・チェックボックス操作時にも変更される為、computedで定義せず、定義済みのrefに都度代入する
watchEffect(() => {
  if (!listUsersRes.value || isListLoading.value) return;

  const users = listUsersRes.value;

  const mappedUsers = users.map(user => ({
    user: {
      email: user.email,
      authority: user.authority,
      auth0UserId: user.auth0UserId ?? '',
      name: user.name ?? '',
      displayName: user.displayName ?? '',
      groupName: user.groups?.map((group: Group) => group.name).join(',') || '',
      groups: user.groups,
      licenses: (user.licenseData ?? []).filter(isNotNullish),
    },
    status: user.status as UserStatus,
    isChecked: false,
    ...(filterOption.value !== 'match' && { isMinus: false }),
  }));

  if (filterOption.value === 'match') {
    licenseAssignedUsers.value = mappedUsers;
  } else {
    licenseNotAssignedUsers.value = mappedUsers;
  }
});

const { pushFeedback } = useFeedback();

//// フィルタリング関係の処理
const searchWord = ref('');

// フィルター後のユーザー一覧
const filteredUser = computed(() => {
  if (searchWord.value) {
    const regex = new RegExp(searchWord.value, 'i');
    return licenseAssignedUsers.value.filter(
      userItem =>
        regex.test(userItem.user.name) ||
        regex.test(userItem.user.email) ||
        regex.test(userItem.user.authority?.displayName)
    );
  }
  return licenseAssignedUsers.value;
});

const noDataText = computed(() => {
  if (
    filteredUser.value.length === 0 &&
    licenseAssignedUsers.value.length !== 0
  ) {
    return messages.license.info.noMatchingUsers;
  } else {
    return messages.license.info.noLicensedUser;
  }
});

const noDataImageUrl = computed(() => {
  if (
    filteredUser.value.length === 0 &&
    licenseAssignedUsers.value.length !== 0
  ) {
    return noSearchDataImage;
  } else {
    return noDataImage;
  }
});

// フィルタリングが動いた際、フィルター外のユーザーのチェックを外す
watchEffect(() => {
  licenseAssignedUsers.value.forEach(user => {
    if (!filteredUser.value.includes(user)) {
      user.isChecked = false;
    }
  });
});

//// チェックボックス関係の処理

// チェックボックスの選択
const onCheckItem = (event: Event, checkedUser: UserItem) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  // チェックされたユーザーの判定は、本来メールアドレスの一致のみで判定できるはず
  // 現状は、アクティブユーザーと同じメールアドレスの招待が作れてしまっているため、アクティブユーザーならauth0UserId,招待ならメールアドレスで判定している
  licenseAssignedUsers.value.forEach(user => {
    if (
      (checkedUser.status === user.status &&
        checkedUser.status === userStatus.active &&
        user.user.auth0UserId === checkedUser.user.auth0UserId) ||
      (checkedUser.status === user.status &&
        checkedUser.status !== userStatus.active &&
        user.user.email === checkedUser.user.email)
    ) {
      user.isChecked = target.checked;
    }
  });

  checkedItemsCount.value = checkedFilterItemsLength();
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  if (checkedDisplayUsersCount === displayUsers.value.length) {
    isThChecked.value = true;
  } else {
    isThChecked.value = false;
  }
};

const checkedFilterItemsLength = () => {
  return filteredUser.value.filter(user => user.isChecked).length;
};

const checkedDisplayItemsLength = () => {
  return displayUsers.value.filter(user => user.isChecked).length;
};

const onCheckHeader = (event: Event) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  licenseAssignedUsers.value.forEach(user => {
    if (displayUsers.value.includes(user)) {
      user.isChecked = target.checked;
    }
  });
  checkedItemsCount.value = checkedFilterItemsLength();
};

const toggleAllChecked = (isAllChecked: boolean) => {
  licenseAssignedUsers.value.forEach(user => {
    if (filteredUser.value.includes(user)) {
      user.isChecked = isAllChecked;
    }
  });
  isThChecked.value = isAllChecked;
  isAllSelected.value = isAllChecked;
};

const { mutate: updateLicenses, loading: isUpdateLicenseLoading } =
  useUpdateLicenses();
useOverlayLoading('isUpdateLicenseLoading', isUpdateLicenseLoading);

// 削除モーダルの表示制御用変数
const isOpenModal = ref(false);
const isError = ref(false);

// ユーザー削除ボタンでモーダル表示
const onClickDeleteModalOpen = () => {
  // 選択されている場合は確認モーダルを表示
  heading.value = messages.license.licenseRelease;
  isOpenModal.value = true;
};

// 削除ボタン押下時の処理
const onClickRemove = async () => {
  // 削除対象のユーザーを絞り込み
  const licenseRemoveUserIds = licenseAssignedUsers.value
    .filter(user => user.isChecked && user.status === userStatus.active)
    .map(user => user.user.auth0UserId);

  // 削除対象の招待を絞り込み
  const licenseRemoveInvitationEmails = licenseAssignedUsers.value
    .filter(
      user =>
        user.isChecked &&
        (user.status === userStatus.invited ||
          user.status === userStatus.expired)
    )
    .map(user => user.user.email);

  const licenseRemoveInput: UpdateLicensesInput = {
    action: Action.remove,
    licenseId: licenseIdref.value,
  };

  if (licenseRemoveUserIds.length > 0) {
    licenseRemoveInput.activeUsers = { auth0UserIds: licenseRemoveUserIds };
  }

  if (licenseRemoveInvitationEmails.length > 0) {
    licenseRemoveInput.invitedUsers = { emails: licenseRemoveInvitationEmails };
  }

  await updateLicense(licenseRemoveInput);
};

const onClickLicenseAssign = async (auth0UserIds: string[]) => {
  isLicenseAssignDialogOpen.value = false;
  if (auth0UserIds && auth0UserIds.length !== 0) {
    const licenseAttachInput = {
      action: Action.add,
      licenseId: licenseIdref.value,
      activeUsers: { auth0UserIds: auth0UserIds },
    };
    await updateLicense(licenseAttachInput);
  }
};

const updateLicense = async (input: UpdateLicensesInput) => {
  const action = input.action;
  try {
    const res = await updateLicenses({ input: input });
    if (res?.data?.updateLicenses?.statusCode === Message.success) {
      isOpenModal.value = false;
      pushFeedback({
        type: 'success',
        heading:
          action === Action.add
            ? messages.license.success.licenseActivationSuccess
            : messages.license.success.licenseReleaseSuccess,
      });
      usersNeeded.value = Users.both;
      filterOption.value = 'match';
      await listRefetch();
      emit('onUpdateLicense');
      return;
    }

    if (res?.data?.updateLicenses?.statusCode === Message.licenseLimitError) {
      isOpenModal.value = true;
      isError.value = true;
      heading.value = messages.license.error.licenseAssignErrorHeading;
      errorText.value = maxLicenseLimitReachedError;
      return;
    }

    if (res?.data?.updateLicenses?.statusCode === Message.authorityLimitError) {
      isOpenModal.value = true;
      isError.value = true;
      heading.value = messages.license.error.licenseAssignErrorHeading;
      errorText.value = maxAuthorityLimitReachedError;
      return;
    }

    throw new Error('failed to update license');
  } catch (error) {
    console.error(error);
    isOpenModal.value = true;
    isError.value = true;
    heading.value =
      action === Action.add
        ? messages.license.error.licenseAssignErrorHeading
        : messages.license.licenseRelease;
    errorText.value =
      action === Action.add ? licenseAssignError : licenseRemoveError;
  }
};

const onClickCancelButton = () => {
  isOpenModal.value = false;
  isError.value = false;
};
const onClickDeleteClose = () => {
  isOpenModal.value = false;
  isError.value = false;
};

const movePage = (pageNumber: number) => {
  currentPage.value = pageNumber;
};
const totalCount = computed(() => {
  checkedItemsCount.value = checkedFilterItemsLength();
  return filteredUser.value.length;
});

watch(
  () => totalCount.value,
  () => {
    const totalPages = Math.ceil(totalCount.value / MAX_DISPLAY_NUM);

    // Adjust current page if needed (After deleting users)
    if (currentPage.value > totalPages) {
      currentPage.value = Math.max(totalPages, 1); // fallback to page 1 if all pages are gone
    }
  }
);

const start = computed(() => (currentPage.value - 1) * MAX_DISPLAY_NUM);
const end = computed(() =>
  Math.min(start.value + MAX_DISPLAY_NUM, totalCount.value)
);

const displayUsers = computed(() =>
  filteredUser.value.slice(start.value, end.value)
);

watchEffect(() => {
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  if (checkedDisplayUsersCount === displayUsers.value.length) {
    isThChecked.value = true;
  } else {
    isThChecked.value = false;
  }
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.LicenseManagement {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__inner {
    margin: $service-unit * 5 0;
  }

  &__header {
    margin: 0 0;
  }

  &__card {
    height: 100%;
    display: flex;
    padding: $service-unit * 4 $service-unit * 8 0;
    background-color: $color-gray-20;
  }

  & &__mainSection {
    padding: $service-unit * 4 $service-unit * 4 $service-unit * 6;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: $color-white-100;
    width: 100%;
  }

  &__inner,
  &__list {
    display: flex;
    align-items: center;
  }
  &__selectedSettings {
    margin-bottom: $service-unit * 7;
  }

  & &__searchForm {
    width: 100% * calc(400 / 1104);
    max-width: $service-unit * 100;
  }

  &__list {
    margin-left: auto;
  }

  &__listItem:not(:first-child) {
    margin-left: $service-unit * 6;
  }

  &__head {
    @include table-head;
    position: sticky;
    top: 0;
    z-index: 2;

    &:first-child {
      width: $service-unit * 11;
    }

    &:nth-child(5),
    &:nth-child(6) {
      width: 100% * calc(140 / 1104);
    }
  }

  & &__button {
    font-size: $service-font-size-ppp;
    min-height: $service-unit * 8;
    margin-bottom: $service-unit * 2;
  }

  & &__pagination {
    margin-top: $service-unit * 5;
  }

  &__modal {
    text-align: center;
  }

  &__modalText {
    font-size: $service-font-size-pp;
    line-height: $service-line-height-mf;
    text-align: center;
    margin-top: $service-unit * 2;
  }

  &__modalButton {
    display: flex;
    justify-content: center;
    margin-top: $service-unit * 4;
  }

  &__modalItem:not(:first-child) {
    margin-left: $service-unit * 3;
  }

  &__icon {
    width: $service-unit * 10;
    margin: $service-unit * 4 $service-unit * 38 0 $service-unit * 38;
    fill: #c33b2e;
  }

  @media (max-width: 767px) {
    & &__searchForm {
      width: 45%;
    }
  }

  @media (max-width: 424px) {
    &__icon {
      margin-left: $service-unit * 28;
    }
  }
}
</style>
